import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Confetti from 'react-confetti';
import Button from '../atoms/Buttons';
import winnerSound from '../../assets/sounds/winner.mp3';
import { isWinnerSoundMuted } from '../../util/winnerSound';
import { couponLabel, winnerName } from './raffleResult';
import { createAudioPlayback } from './audioPlayback';
import ActivateSoundButton from './ActivateSoundButton';

const Overlay = styled.div`
  position: absolute; inset: 0; z-index: 2; pointer-events: none;
  .actions {
    position: absolute; z-index: 2; bottom: clamp(12px, 3vh, 32px); left: 0; right: 0;
    display: flex; flex-direction: column; align-items: center; gap: 10px;
    padding: 0 16px; text-align: center;
  }
  .buttons { display: flex; justify-content: center; gap: clamp(12px, 4vw, 64px); pointer-events: auto; }
  .buttons button { font-family: 'Open Sans', sans-serif; }
  .error { margin: 0; max-width: 640px; padding: 8px 14px; background: #190e09ed; color: #fff0d7; }
  .heading {
    position: absolute; z-index: 2; top: clamp(10px, 2.5vh, 28px); left: 0; right: 0; margin: 0; text-align: center;
    font: 800 clamp(26px, 4.6vw, 62px)/1.1 Arial, sans-serif; letter-spacing: -.04em; color: #ffe9a4;
    text-shadow: 0 1px #ffd875, 0 3px #b8791a, 0 5px #72300d, 0 8px 14px #280000;
    animation: heading-in .7s ease-out both;
  }
  @keyframes heading-in { from { opacity: 0; transform: translateY(-14px) scale(.92); } to { opacity: 1; transform: none; } }
  @media (prefers-reduced-motion: reduce) { .heading { animation: none; } }
  .announcement { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
  @media (max-width: 480px) { .buttons button { padding: 12px 16px; letter-spacing: .03em; } }
`;

export default function RaffleWinnerOverlay({ winner, salaId, onRestart, onConfirm, confirming = false, confirmed = false, showActions = true, error, headingDelayMs = 0 }) {
  const area = useRef(null);
  const playback = useRef(null);
  const [soundBlocked, setSoundBlocked] = useState(false);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [reducedMotion, setReducedMotion] = useState(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
  );
  useEffect(() => {
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!media) return undefined;
    const change = () => setReducedMotion(media.matches);
    media.addEventListener('change', change);
    return () => media.removeEventListener('change', change);
  }, []);
  useEffect(() => {
    const node = area.current;
    const resize = () => setSize({ width: node.clientWidth, height: node.clientHeight });
    resize();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resize);
    observer?.observe(node);window.addEventListener('resize', resize);
    return () => { observer?.disconnect();window.removeEventListener('resize', resize); };
  }, []);
  // El aviso de sonido bloqueado es de la sala anterior: se limpia al cambiar de sala.
  const [soundFor, setSoundFor] = useState(salaId);
  if (salaId !== soundFor) {
    setSoundFor(salaId);
    setSoundBlocked(false);
  }
  useEffect(() => {
    if (isWinnerSoundMuted(salaId)) return;
    const audio = new Audio(winnerSound);
    const sound = createAudioPlayback(audio, setSoundBlocked);
    playback.current = sound;sound.play();
    return () => { sound.dispose();playback.current = null; };
  }, [salaId]);
  return <Overlay ref={area} aria-label="Resultado del sorteo">
    {soundBlocked && <ActivateSoundButton onClick={() => playback.current?.play()}>Activar sonido</ActivateSoundButton>}
    {!reducedMotion && <Confetti width={size.width} height={size.height}
      numberOfPieces={100} gravity={0.065} wind={0.015}
      colors={['#ffd25a', '#f5b51b', '#fff0a4', '#c60019', '#f22b35']}
      opacity={0.9} style={{ zIndex: 1, pointerEvents: 'none' }} />}
    <h2 className="heading" aria-hidden="true" style={{ animationDelay: `${headingDelayMs}ms` }}>¡FELICIDADES!</h2>
    <p className="announcement" role="status">Ganador: {winnerName(winner) || 'Cliente anónimo'}. Cupón: {couponLabel(winner)}.</p>
    {showActions && <div className="actions">
      {error && <p className="error" role="alert">{error}</p>}
      <div className="buttons">
        <Button variant="outline" onClick={onRestart} disabled={confirming}>REINICIAR</Button>
        <Button onClick={onConfirm} disabled={confirming || confirmed}>
          {confirming ? 'CONFIRMANDO…' : confirmed ? 'CONFIRMADO' : 'CONFIRMAR'}
        </Button>
      </div>
    </div>}
  </Overlay>;
}
